import api from '@/lib/axios';

export interface ChecklistTaskState {
  done: boolean;
  /** True when nothing was ticked by hand and a detector found it already done. */
  auto: boolean;
  completedBy: string | null;
  completedAt: string | null;
}

export interface ChecklistAssignment {
  user: { _id: string; name: string; position?: string } | null;
  assignedAt?: string;
  tourDoneAt?: string | null;
  /** Set when the section's last task was ticked. */
  completedAt?: string | null;
}

export interface ChecklistState {
  /**
   * False for stores that predate onboarding — they have no setup record and
   * the checklist doesn't apply to them. Everything else comes back empty.
   */
  active: boolean;
  /** 'assignee' sees only the sections handed to them. */
  role: 'admin' | 'assignee';
  /** Section ids this caller may see, already filtered by subscription. */
  sectionIds: string[];
  /** Section ids assigned to this caller (an admin can be an assignee too). */
  mySections: string[];
  tasks: Record<string, ChecklistTaskState>;
  assignments: Record<string, ChecklistAssignment>;
  hasDemoData: boolean;
}

export interface MyAssignments {
  sections: string[];
  /** Assigned sections whose scoped tour hasn't been run or skipped yet. */
  needsIntro: string[];
}

export const setupChecklistService = {
  get: async (): Promise<ChecklistState> => {
    const { data } = await api.get('/api/setup/checklist');
    return data;
  },

  myAssignments: async (): Promise<MyAssignments> => {
    const { data } = await api.get('/api/setup/checklist/my-assignments');
    return data;
  },

  setTask: async (taskId: string, done: boolean) => {
    const { data } = await api.patch(`/api/setup/checklist/tasks/${taskId}`, { done });
    return data;
  },

  assignSection: async (sectionId: string, userId: string | null) => {
    const { data } = await api.put(
      `/api/setup/checklist/sections/${sectionId}/assignment`,
      { userId }
    );
    return data;
  },

  markTourDone: async (sectionId: string) => {
    const { data } = await api.post(`/api/setup/checklist/sections/${sectionId}/tour-done`);
    return data;
  }
};
